import type { WellnessInsightsComponentDefinitionExtension as WellnessInsightsComponentDefinition } from 'tio-data/data/resources/wellness-insights-component-definition.ext';
import DashboardItem from 'tio-employee/components/wellness-insights/dashboard-item';
import Score from 'tio-employee/components/wellness-insights/score';
import Summary from 'tio-employee/components/wellness-insights/summary';
import Recommendation from 'tio-employee/components/wellness-insights/recommendation';
import AccountCreationPrompt from 'tio-employee/components/wellness-insights/account-creation-prompt';

// use these to ensure sizing consistency across wellness insights dash component icons
export const dashboardIconSizes = {
  md: 72,
  lg: 144,
};

/**
 * look up and return a wellness insights dashboard component given a component definition, via
 * specified component 'type'
 *
 * @param {WellnessInsightsComponentDefinition} definition - wellness train component definition object
 * @returns {DashboardItem|undefined} constructor for a DashboardItem descendent component
 */
export const getDashComponent = (
  definition: WellnessInsightsComponentDefinition
): typeof DashboardItem => {
  switch (definition?.type) {
    case 'score':
      return Score;
    case 'add_account':
    case 'doesnt_have_loans':
    case 'not_adding_loans':
      return AccountCreationPrompt;
    case 'dash_summary':
      return Summary;
    case 'to_coaching':
    case 'to_pslf_tool':
    case 'to_idr_tool':
    case 'to_nslds_upload':
    case 'to_refinancing_tool':
    case 'to_strategy_finder':
    case 'to_pslf_form_submission':
    case 'transitional_strategy_finder_results':
    case 'transitional_pslf_form_submission':
    case 'transitional_pslf_tool_results':
    case 'transitional_idr_results':
      return Recommendation;
    default:
      throw new Error(
        `WellnessInsightsComponentDefinition id ${definition.id} type invalid for component lookup`
      );
  }
};

/**
 * look up and return link data for a wellness insights dashboard recommendation component, via
 * specified component 'name'. this logic assumes recommendation components will only ever have
 * one spot for a cta style link, which *should* stay true but you never know
 *
 * @param {WellnessInsightsComponentDefinition} definition - wellness train component definition object
 * @returns {{ to: string, query: Record<string, unknown> }} constructor for a DashboardItem descendent component
 */
export const getRecommendationCtaRoute = (
  definition: WellnessInsightsComponentDefinition
): { to: string; query?: Record<string, string> } => {
  switch (definition?.name) {
    case 'recommend_coaching_consolidate_parent_plus':
    case 'recommend_coaching':
    case 'recommend_coaching_your_benefit':
    case 'recommend_coaching_short':
    case 'recommend_coaching_pslf':
    case 'recommend_coaching_extra_payment':
    case 'recommend_coaching_affordability':
    case 'recommend_coaching_consolidation':
    case 'recommend_coaching_parent_plus':
    case 'recommend_coaching_consolidation_direct':
    case 'recommend_coaching_affordability_pslf':
    case 'recommend_coaching_consolidation_ffel':
    case 'recommend_coaching_consolidation_pslf':
    case 'recommend_coaching_consolidation_perkins':
      return { to: 'authenticated.repaying-student-debt.coaching.schedule' };
    case 'recommend_refinancing_tool':
    case 'recommend_refinancing_tool_maybe':
      return { to: 'authenticated.repaying-student-debt.repayment-options.refinancing' };
    case 'recommend_pslf_form_submission_unique':
    case 'transition_pslf_form':
      return { to: 'https://studentaid.gov/pslf/' };
    case 'recommend_nslds_upload':
      return {
        to: 'authenticated.observability.upload',
        query: { source: 'wellness', providers: 'nslds,statement' },
      };
    case 'recommend_idr_tool_now':
    case 'recommend_idr_tool':
    case 'transition_idr_tool':
      return { to: 'authenticated.idr.dashboard' };
    case 'recommend_pslf_form_submission':
    case 'recommend_pslf_form_submission_also':
      return { to: 'authenticated.pslf.dashboard' };
    case 'recommend_pslf_tool':
    case 'recommend_pslf_tool_before_payment':
    case 'recommend_pslf_tool_during_payment':
    case 'transition_pslf_tool':
      return { to: 'authenticated.pslf.dashboard.forgiveness-estimator' };
    case 'recommend_strategy_finder_now':
    case 'recommend_strategy_finder':
    case 'recommend_strategy_finder_short':
    case 'recommend_strategy_finder_pslf':
    case 'transition_strategy_finder':
      return { to: 'authenticated.repaying-student-debt.repayment-strategy-finder' };
    default:
      throw new Error(
        `WellnessInsightsComponentDefinition id ${definition.id} name invalid for link lookup`
      );
  }
};
